import React, { useEffect, useMemo, useState } from "react"

import { format } from "date-fns"

import EventFilters from "@/components/common/EventFilters"
import EventPageHeader from "@/components/common/EventPageHeader"
import OrientationEventDisplay from "@/components/orientation/OrientationEventDisplay"
import { Skeleton } from "@/components/ui/skeleton"
import { useDepots } from "@/hooks/useDepots"
import { useOrientationEvents } from "@/hooks/useOrientationEvents"
import { JobPosting } from "@/types/jobPosting"

import CreateOrientationDialog from "./CreateOrientationDialog"

interface OrientationContentProps {
  jobPostings: JobPosting[]
}

const OrientationContent: React.FC<OrientationContentProps> = ({ jobPostings }) => {
  // Get depot/location data from the database
  const { data: depots, isLoading: isLoadingDepots } = useDepots()

  // Format job postings for use in the dialog (only active jobs)
  const formattedJobPostings = useMemo(() => {
    if (!jobPostings) return []
    return jobPostings
      .filter((job) => job.active) // Only show active jobs
      .map((job) => ({
        label: job.title,
        value: job.id,
      }))
  }, [jobPostings])

  // Format locations for use in the dialog
  const formattedLocations = useMemo(() => {
    const locationsArray = depots.map((depot) => ({
      label: `${depot.name} (${depot.depot_id})`,
      value: depot.id,
    }))
    return [{ label: "All Locations", value: "all" }, ...locationsArray]
  }, [depots])
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid")
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<string>("all")
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)

  // Create filter object for useSessions
  const [sessionFilters, setSessionFilters] = useState({
    depot_id: selectedLocation !== "all" ? selectedLocation : undefined,
    date_from: selectedDate ? format(selectedDate, "yyyy-MM-dd") : undefined,
    date_to: selectedDate ? format(selectedDate, "yyyy-MM-dd") : undefined,
    status: "Open" as "Open" | "Closed" | "Deleted" | undefined,
  })

  // Update filters when selections change
  useEffect(() => {
    setSessionFilters({
      depot_id: selectedLocation !== "all" ? selectedLocation : undefined,
      date_from: selectedDate ? format(selectedDate, "yyyy-MM-dd") : undefined,
      date_to: selectedDate ? format(selectedDate, "yyyy-MM-dd") : undefined,
      status: "Open",
    })
  }, [selectedLocation, selectedDate])

  const {
    events,
    newEvent,
    setNewEvent,
    handleCreateEvent,
    // Make these available in the component
    createSession,
  } = useOrientationEvents({
    jobPostings: formattedJobPostings,
    locations: formattedLocations,
    filters: sessionFilters,
  })

  // Already filtered by the hook, but we can add additional frontend filtering if needed
  const filteredEvents = events

  // Handle setting the viewMode with type safety
  const handleSetViewMode = (mode: "grid" | "table" | "kanban" | "calendar") => {
    // Only accept grid or table modes
    if (mode === "grid" || mode === "table") {
      setViewMode(mode)
    }
  }

  return (
    <>
      <EventPageHeader
        title="Orientation Events"
        viewMode={viewMode}
        setViewMode={handleSetViewMode}
        isCreateDialogOpen={isCreateDialogOpen}
        setIsCreateDialogOpen={setIsCreateDialogOpen}
        createButtonLabel="Create Orientation"
        toggleType="grid-table"
      >
        {isLoadingDepots ? (
          <div className="p-4 flex flex-col gap-4 min-h-[300px] min-w-[400px] justify-center items-center">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : (
          <CreateOrientationDialog
            newEvent={newEvent}
            setNewEvent={setNewEvent}
            handleCreateEvent={() => {
              handleCreateEvent()
              setIsCreateDialogOpen(false)
            }}
            jobPostings={formattedJobPostings}
            locations={formattedLocations.filter(
              (loc) => loc.value !== "all",
            )} /* Exclude 'All Locations' */
            isLoading={createSession.isPending || false}
            error={createSession.error as Error | null}
          />
        )}
      </EventPageHeader>

      <EventFilters
        locations={formattedLocations}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />

      <OrientationEventDisplay
        filteredEvents={filteredEvents}
        viewMode={viewMode}
        jobPostings={jobPostings}
      />
    </>
  )
}

export default OrientationContent
